<!--
    Component Name: CareCommunity Editor
    Summary:  Create table with only one active membership, able to have non consecutive memberships.
    Properties:
-->
<template>
    <div class="CareCommunityEditor">
        <div v-if="CheckPermission(ViewPermissionRequirement)">
            <b-container>
                <b-row style="overflow-x:auto;">
                    <b-col cols="11">
                        <div v-if="SaveOriginalArray.length > 10">
                            <div v-if="ToggleNumberOfRecordsShownValue == false">
                                <font-awesome-icon :icon="['fa', 'toggle-off']" v-on:click="ToggleNumberOfRecordsShown()" class="pointer" />
                            </div>
                            <div v-else>
                                <font-awesome-icon :icon="['fa', 'toggle-on']" v-on:click="ToggleNumberOfRecordsShown()" class="pointer" />
                            </div>
                        </div>
                        <vuetable :fields="Fields"
                                  :data="LoadedRecords"
                                  pagination-path=""
                                  :api-mode="false"
                                  class="vue-datatable"
                                  ref="datatable"
                                  :row-class="onRowClass">
                            <template slot="EditRecord" scope="props">
                                <div class="EditRecord" v-if="EditMode">
                                    <span class="underline pointer" @click="ShowDetailView(props.rowData)"><span>{{ props.rowData.CareCommunity.Name }}</span> <font-awesome-icon :icon="['fa', 'edit']" /></span>
                                </div>
                                <div v-else>
                                    <span>{{ props.rowData.CareCommunity.Name }}</span>
                                </div>
                            </template>
                        </vuetable>
                    </b-col>
                    <b-col cols="1">
                        <div v-if="EditMode && CheckPermission(EditPermission)" class="vert-icons">
                            <b-row>
                                <font-awesome-icon :icon="['fa', 'plus']" v-on:click="ShowAddRecord()" class="pointer olive-green-text" />
                            </b-row>
                        </div>
                    </b-col>
                </b-row>
            </b-container>

            <!--ADD NEW RECORD MODAL-->
            <b-modal ref="add-modal" @hide="EndRecordDate = '';EffectiveDate = ''" :title="AddRecordModalTitle" hide-footer>
                <b-container>
                    <b-form @submit="AddRecord">
                        <b-row>
                            <b-col>
                                <div v-if="TableState == 'OneOpen'" class="form-group">
                                    <b>
                                        (NOTE: Only one Care Community can be active at one time)
                                    </b>
                                    <br />
                                    <label>Ending Date for: {{FieldData.filter(datalist => datalist.EndDate == null)[0].CareCommunity.Name}} </label>
                                    <flat-pickr v-model="EndRecordDate" class="form-control" ref="showfocus" required :config="DateConfig"></flat-pickr>
                                </div>
                                <div class="form-group">
                                    <label>{{ SelectLabel }}</label>
                                    <select class="form-control" v-model="NewRecordValue" required>
                                        <option v-for="option in NewRecordOptions" v-bind:value="option.value">
                                            {{ option.text }}
                                        </option>
                                    </select>
                                </div>
                                <div class="form-group">
                                    <label>Starting Date:</label>
                                    <flat-pickr v-model="EffectiveDate" class="form-control" ref="showfocus" required :config="DateConfig"></flat-pickr>
                                </div>
                            </b-col>
                        </b-row>
                        <b-row class="modal-btn-row">
                            <div class="modal-btn-row-div">
                                <b-button size="md" variant="secondary" @click="CancelAddRecord()">
                                    CANCEL
                                </b-button>
                                <b-button size="md" variant="success" type="submit">
                                    SAVE
                                </b-button>
                            </div>
                        </b-row>
                    </b-form>
                </b-container>
            </b-modal>

            <!--ADD AND EDIT AND DETAIL MODAL-->
            <b-modal ref="edit-modal" @hide="TargetRecord = {}" :title="EditModalTitle" hide-footer>
                <b-container>
                    <b-form @submit="EditRecord">
                        <b-row>
                            <b-col>
                                <div class="form-group">
                                    <label>Start Date:</label>
                                    <flat-pickr v-model="TargetRecord.StartDate" class="form-control" required :config="DateConfig"></flat-pickr>
                                </div>
                                <div class="form-group">
                                    <label>End Date:</label>
                                    <flat-pickr v-model="TargetRecord.EndDate" class="form-control" :config="DateConfig"></flat-pickr>
                                </div>
                            </b-col>
                        </b-row>
                        <b-row class="modal-btn-row">
                            <div class="modal-btn-row-div">
                                <b-button size="md" variant="secondary" @click="$refs['edit-modal'].hide()">
                                    CANCEL
                                </b-button>
                                <b-button size="md" variant="success" type="submit" v-if="EditMode">
                                    SAVE
                                </b-button>
                                <b-button size="md" variant="danger" @click="DeleteRecord()" type="button" v-if="TargetRecord.Id">
                                    DELETE
                                </b-button>
                            </div>
                        </b-row>
                    </b-form>
                </b-container>
            </b-modal>
            <!--DELETE CURRENT RECORD MODAL-->
            <b-modal ref="delete-modal" :title="DeleteRecordModalTitle" hide-footer>
                <b-container>
                    <b-form @submit="DeleteRecord">
                        <b-row>
                            <b-col>
                                <p>{{ DeleteRecordModalText }}</p>
                            </b-col>
                        </b-row>
                        <b-row class="modal-btn-row">
                            <div class="modal-btn-row-div">
                                <b-button size="md" variant="secondary" @click="CancelDeleteRecord()">
                                    CANCEL
                                </b-button>
                                <b-button size="md" variant="danger" type="submit">
                                    DELETE
                                </b-button>
                            </div>
                        </b-row>
                    </b-form>
                </b-container>
            </b-modal>
        </div>
        <div v-else>
            PRIVATE
        </div>
    </div>

</template>

<script>
    //import icons
    import { library } from '@fortawesome/fontawesome-svg-core';
    import { faPlus } from '@fortawesome/free-solid-svg-icons';
    library.add(faPlus);
    import { faExchangeAlt } from '@fortawesome/free-solid-svg-icons';
    library.add(faExchangeAlt);
    import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
    library.add(faTrashAlt);

    import { faToggleOff } from '@fortawesome/free-solid-svg-icons';
    library.add(faToggleOff);
    import { faToggleOn } from '@fortawesome/free-solid-svg-icons';
    library.add(faToggleOn);
    //import components
    import Vuetable from 'vuetable-2/src/components/Vuetable';
    import flatPickr from 'vue-flatpickr-component';
    import 'flatpickr/dist/flatpickr.css';
    export default {
        props: [

            "Fields",
            "FieldData",
            "SelectLabel",
            "OptionsMethod",
            "OptionsMethodRecordId",
            "AddRecordMethod",
            "AddRecordRecordPropertyName",
            "AddRecordAdditionalPayload",
            "AddRecordModalTitle",

            "EndRecordMethod",
            "EndRecordId",
            "UpdateRecordMethod",
            "GetPayload",
            "GetEndpoint",

            "DeleteRecordMethod",
            "DeleteRecordModalTitle",
            "DeleteRecordModalText",

            "PropertyName",
            "RecordId",
            "EditMode",
            "DataLabel",
            "Required",
            "Options",
            "EditPermission",
            "ViewPermissionRequirement",

            "ChartId",
        ],
        data: function () {
            return {
                ToggleNumberOfRecordsShownValue: false,
                SaveOriginalArray: [],
                NewRecordValue: "",
                NewRecordOptions: {},
                TargetRecord: {},
                EditModalTitle: "",
                EffectiveDate:"",
                EndRecordDate: "",
                LoadedRecords: [],
                DateConfig: {
                    dateFormat: 'Y-m-d',
                    allowInput: true,
                    altFormat: 'm/d/Y',
                    altInput: true, 
                }
            };
        },
        methods: {
            RefreshDataTable() {
                this.$refs.datatable.refresh();
            },
            onRowClass(dataItem, index) {
                return (dataItem.EndDate == null) ? 'current-record' : '';
            },
            GetRecords() {
                this.$MasterAPI.ExecuteRequest("GET", this.GetEndpoint, this.GetPayload, this.GetRecordsSuccess, () => { }, false);
            },
            GetRecordsSuccess(response) {
                this.LoadedRecords = response.data.data;
            },
            //ADD RECORD
            ShowAddRecord() {
                this.NewRecordValue = "";
                this.$MasterAPI.ExecuteRequest("GET", this.OptionsMethod, { Id: this.OptionsMethodRecordId }, this.ShowAddRecordSuccess, () => { }, false);
            },
            ShowAddRecordSuccess(response) {
                console.log(response);
                this.$set(this, "NewRecordOptions", response.data.data);
                this.$refs['add-modal'].show();
            },
            AddRecord(e) {
                e.preventDefault();
                var vm = this;
                let initialPayload = { Id: this.RecordId, [this.AddRecordRecordPropertyName]: this.NewRecordValue, StartDate: this.EffectiveDate, EndDate: this.EndRecordDate };
                let finalPayload = { ...initialPayload, ...this.AddRecordAdditionalPayload };
                this.$MasterAPI.ExecuteRequest("POST", this.AddRecordMethod, finalPayload, this.AddRecordSuccess, () => { }, true);

            },
            AddRecordSuccess(response) {
                this.GetRecords();
                //Perform Global Updates
                this.$GlobalUpdate.ExecuteGlobalUpdate(this.ChartId);
                this.$refs['add-modal'].hide();
            },
            CancelAddRecord() {
                this.$refs['add-modal'].hide();
            },
            //End Record
            EndRecord() {
                this.$MasterAPI.ExecuteRequest("POST", this.EndRecordMethod, { Id: this.EndRecordId, DateTime: this.EndRecordDate }, this.EndRecordSuccess, () => { }, false);
            },
            EndRecordSuccess() {
                this.$refs['end-record-modal'].hide();
            },

            CancelTransferRecord() {
                this.$refs['transfer-modal'].hide();
            },
            //DELETE RECORD
            ShowDeleteRecord() {
                this.$refs['delete-modal'].show();
            },
            DeleteRecord(e) {
                this.$MasterAPI.ExecuteRequest("POST", this.DeleteRecordMethod, { Id: this.TargetRecord.Id }, this.DeleteRecordSuccess, () => { }, true);
            },
            DeleteRecordSuccess(response) {
                this.GetRecords();
                //Perform Global Updates
                this.$GlobalUpdate.ExecuteGlobalUpdate(this.ChartId);
                this.$refs['edit-modal'].hide();
            },
            CancelDeleteRecord() {
                this.$refs['delete-modal'].hide();
            },

            CheckPermission(permissionName) {
                return this.$store.getters["User.PrivateProfile.CheckPermission"](permissionName);
            },
            formatDate(value, fmt = 'D MMM YYYY', defaultValue = '') {
                return (value == null)
                    ? defaultValue
                    : moment(value, 'YYYY-MM-DD').format(fmt)
            },
            ToggleNumberOfRecordsShown() {
                this.ToggleNumberOfRecordsShownValue = !this.ToggleNumberOfRecordsShownValue;
                if (this.ToggleNumberOfRecordsShownValue) {
                    this.FieldData = this.FieldData.slice(0, 10);
                }
                else {
                    this.FieldData = this.SaveOriginalArray;
                }
            },
            EditRecord(e) {
                e.preventDefault();
                //get person data here
                this.$MasterAPI.ExecuteRequest("POST", this.UpdateRecordMethod, { Id: this.TargetRecord.Id, StartDate: this.TargetRecord.StartDate, EndDate: this.TargetRecord.EndDate }, this.EditRecordSuccess, () => { }, true);
            },
            EditRecordSuccess(response) {
                this.GetRecords();
                
                //Perform Global Updates
                this.$GlobalUpdate.ExecuteGlobalUpdate(this.ChartId);
                this.$refs["edit-modal"].hide();
            },
            ShowDetailView(record) {
                this.TargetRecord = JSON.parse(JSON.stringify(record));
                this.EditModalTitle = "Editing " + record.CareCommunity.Name;
                this.$refs["edit-modal"].show();
            },
        },
        components: {
            Vuetable,
            flatPickr
        },
        mounted() {
            this.SaveOriginalArray = this.FieldData;
            this.ToggleNumberOfRecordsShown();
            this.GetRecords();
        },
        computed: {
            ModalTitle: function () {
                return "Editing " + this.DataLabel;
            },
            TableData: function () {
                return this.LoadedRecords;
            },
            TableState: function () {
                if (this.FieldData.length == 0) {
                    return "NoRecords";
                }
                else if (this.FieldData.filter(datalist => datalist.EndDate == null).length == 0) {
                    return "NoRecords";
                } else {
                    return "OneOpen";
                }

            },
        },
    }
</script>

<style scoped>
    .vert-icons div {
        margin-top: 10px;
    }

    /deep/ tbody tr.current-record {
        font-weight: bold;
    }

    table {
        table-layout: auto;
    }

    .vue-datatable {
        width: 100%;
        display: inline-table;
        overflow-x: auto;
    }
</style>